import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MessagesSquare, UserCheck, Bot, Send, Pencil, Loader2, Paperclip, RefreshCw } from 'lucide-react';

const STATI = { aperta: 'bg-emerald-100 text-emerald-700', 'in carico staff': 'bg-amber-100 text-amber-700', chiusa: 'bg-slate-200 text-slate-500' };

export const ConversazioniAI = () => {
  const [convs, setConvs] = useState([]);
  const [filtroStato, setFiltroStato] = useState('');
  const [sel, setSel] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [correggendo, setCorreggendo] = useState(null);
  const [correzione, setCorrezione] = useState('');

  const load = async () => {
    try {
      const r = await api.get(`/ai-admin/conversazioni${filtroStato ? `?stato=${encodeURIComponent(filtroStato)}` : ''}`);
      setConvs(r.data.conversazioni || []);
    } catch { toast.error('Errore caricamento conversazioni'); }
  };
  useEffect(() => { load(); }, [filtroStato]);

  const apri = async (c) => {
    try {
      const r = await api.get(`/ai-admin/conversazioni/${c.id}`);
      setSel(r.data);
    } catch { toast.error('Errore'); }
  };

  const prendiInCarico = async () => {
    try {
      await api.post(`/ai-admin/conversazioni/${sel.id}/prendi-in-carico`);
      toast.success('Presa in carico: l\'AI è in pausa su questa chat');
      apri(sel); load();
    } catch { toast.error('Errore'); }
  };

  const restituisci = async () => {
    try {
      await api.post(`/ai-admin/conversazioni/${sel.id}/restituisci`);
      toast.success('Restituita all\'AI');
      apri(sel); load();
    } catch { toast.error('Errore'); }
  };

  const invia = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/ai-admin/conversazioni/${sel.id}/reply`, { testo: reply.trim() });
      toast.success('Messaggio inviato su WhatsApp');
      setReply(''); apri(sel);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore invio'); }
    finally { setSending(false); }
  };

  const salvaCorrezione = async (msg, idx) => {
    if (!correzione.trim()) { toast.error('Scrivi la risposta corretta'); return; }
    const prev = (sel.messaggi || []).slice(0, idx).reverse().find(m => m.who === 'cliente');
    try {
      await api.post(`/ai-admin/conversazioni/${sel.id}/correggi`, {
        domanda: prev?.testo || '', risposta_corretta: correzione.trim(),
      });
      toast.success('Correzione salvata come nota "da approvare"');
      setCorreggendo(null); setCorrezione('');
    } catch { toast.error('Errore'); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-4" data-testid="conversazioni-ai">
      <Card className="lg:col-span-2">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2"><MessagesSquare size={15} /> Conversazioni AI</CardTitle>
            <Button size="sm" variant="ghost" onClick={load} data-testid="conv-refresh"><RefreshCw size={13} /></Button>
          </div>
          <div className="flex gap-1.5 flex-wrap pt-1">
            {['', 'aperta', 'in carico staff', 'chiusa'].map(s => (
              <button key={s} onClick={() => setFiltroStato(s)}
                className={`text-[11px] px-2.5 py-1 rounded-full border ${filtroStato === s ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-500'}`}
                data-testid={`conv-filtro-${s || 'tutte'}`}>{s || 'Tutte'}</button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-1.5 max-h-[520px] overflow-y-auto">
          {convs.length === 0 && <p className="text-xs italic text-slate-400">Nessuna conversazione archiviata.</p>}
          {convs.map(c => (
            <button key={c.id} onClick={() => apri(c)}
              className={`w-full text-left border rounded-lg p-2.5 text-xs hover:border-violet-300 ${sel?.id === c.id ? 'border-violet-400 bg-violet-50/50' : 'bg-slate-50/60'}`}
              data-testid={`conv-${c.id}`}>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-semibold">{c.nome || c.numero}</span>
                {c.is_test && <Badge className="border-0 text-[9px] bg-fuchsia-100 text-fuchsia-700">TEST</Badge>}
                <Badge className={`border-0 text-[9px] ${STATI[c.stato] || ''}`}>{c.stato}</Badge>
                {c.tipo_richiesta && <Badge className="border-0 text-[9px] bg-blue-100 text-blue-700">{c.tipo_richiesta}</Badge>}
              </div>
              <p className="text-slate-400 mt-0.5">{c.numero} — {c.last_msg_at?.slice(0, 16).replace('T', ' ')} — consenso: {c.consenso ? 'sì' : 'no'}</p>
            </button>
          ))}
        </CardContent>
      </Card>

      <Card className="lg:col-span-3">
        {!sel ? (
          <CardContent className="py-16 text-center text-sm text-slate-400 italic">Seleziona una conversazione</CardContent>
        ) : (
          <>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-sm">{sel.nome || sel.numero} <span className="text-slate-400 font-normal">({sel.numero})</span></CardTitle>
                <div className="flex gap-2">
                  {sel.stato === 'in carico staff' ? (
                    <Button size="sm" variant="outline" onClick={restituisci} className="h-8 text-xs border-emerald-300 text-emerald-700" data-testid="conv-restituisci-btn">
                      <Bot size={13} className="mr-1" /> Restituisci all'AI
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={prendiInCarico} className="h-8 text-xs border-amber-300 text-amber-700" data-testid="conv-prendi-btn">
                      <UserCheck size={13} className="mr-1" /> Prendi in carico
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1 bg-slate-50/50 rounded-lg p-3" data-testid="conv-messaggi">
                {(sel.messaggi || []).map((m, i) => (
                  <div key={i} className={`flex ${m.who === 'cliente' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-xs ${m.who === 'cliente' ? 'bg-white border' : m.who === 'staff' ? 'bg-amber-100 text-amber-900' : 'bg-violet-600 text-white'}`}>
                      <p className="text-[9px] opacity-60 font-semibold">{m.who === 'ai' ? 'Sofia (AI)' : m.who === 'staff' ? `Staff${m.autore ? ` (${m.autore})` : ''}` : 'Cliente'}</p>
                      <p className="whitespace-pre-wrap">{m.testo}{m.has_media && <span className="inline-flex items-center gap-1 ml-1 opacity-70"><Paperclip size={10} />{m.media_type}</span>}</p>
                      {m.who === 'ai' && (
                        <button onClick={() => { setCorreggendo(i); setCorrezione(''); }} className="text-[10px] underline opacity-80 mt-1 flex items-center gap-1" data-testid={`conv-correggi-${i}`}>
                          <Pencil size={9} /> Correggi
                        </button>
                      )}
                      {correggendo === i && (
                        <div className="mt-1.5 space-y-1">
                          <Textarea rows={3} value={correzione} onChange={e => setCorrezione(e.target.value)}
                            placeholder="Scrivi la risposta giusta..." className="text-xs bg-white text-slate-800" data-testid="conv-correzione-input" />
                          <div className="flex gap-1.5">
                            <Button size="sm" className="h-6 text-[10px] bg-emerald-600 text-white" onClick={() => salvaCorrezione(m, i)} data-testid="conv-correzione-salva">Salva come nota da approvare</Button>
                            <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setCorreggendo(null)}>Annulla</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={reply} onChange={e => setReply(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') invia(); }}
                  placeholder="Rispondi come staff (inviato su WhatsApp)..." data-testid="conv-reply-input" />
                <Button onClick={invia} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="conv-reply-send">
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </Button>
              </div>
              <p className="text-[10px] text-slate-400">Le risposte dello staff vengono salvate nella conversazione. Se non hai "preso in carico", l'AI potrebbe continuare a rispondere.</p>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};
